import { CONTRACTS_REVIEW } from '../../constants/actions';
import { apiActionCreator } from '../helpers';
import { contracts } from '../../services/api';
import { openFlash } from '../global';
import { contractSelector, reviewDataSelector } from '../../selectors/contracts/review';
import { push } from 'react-router-redux';
import { CONTRACTS_PATH, REVIEW_SUCCESS_PATH, ROOT_PATH } from '../../constants/routes';
import {
    CONTRACT_STATE_SIGNED,
    CONTRACT_STATE_FEEDBACK,
    CONTRACT_STATE_SENT_DRAFT,
    CONTRACT_TYPES
} from '../../constants/settings';
import { currentUserSelector } from '../../selectors/authentication';
import {
    FORBIDDEN_CODE,
    UNPROCESSABLE_ENTITY_CODE,
    NOT_FOUND_CODE,
    NOT_ACCEPTABLE_CODE
} from '../../constants/http.codes';
import { forbiddenAction } from '../error.handling';
import { find, get, omit } from 'lodash';
import { permitContractBody } from '../admin/models/contracts';
import { timeToMs } from '../../lib/date.utils';
import { bufferArrayToUtf8 } from '../../lib/string.utils';

export function getMember(id, publicLink = false, onlyType = false, pin) {
    return async dispatch => {
        dispatch({ type: CONTRACTS_REVIEW.FETCH_START });
        await apiActionCreator(
            dispatch,
            () => {
                if (publicLink && onlyType) {
                    return contracts.getPublicOnlyType(id);
                }
                if (publicLink) {
                    return contracts.getPublic(id, pin);
                }
                return contracts.get(id);
            },
            response => {
                if (response.prefetch === true && response.type === CONTRACT_TYPES.CLEARANCE) {
                    return getMember(id, publicLink, false)(dispatch);
                }
                if (response.version) {
                    response.version.logo = bufferArrayToUtf8(
                        (response.version.logo && response.version.logo.data) || []
                    );
                }
                return dispatch({
                    type: CONTRACTS_REVIEW.FETCH_OK,
                    contract: { ...response }
                });
            },
            {
                [UNPROCESSABLE_ENTITY_CODE]: () => {
                    dispatch(push(CONTRACTS_PATH));
                    dispatch(openFlash('Contract not found.', 'error'));
                },
                [NOT_FOUND_CODE]: () => {
                    dispatch(push(CONTRACTS_PATH));
                    dispatch(openFlash('Contract not found.', 'error'));
                },
                [FORBIDDEN_CODE]: resp => {
                    if (resp.code !== 'INVALID_PIN') {
                        dispatch(push(ROOT_PATH));
                        dispatch(forbiddenAction());
                    }
                    dispatch({
                        type: CONTRACTS_REVIEW.FETCH_OK,
                        contract: { pinError: true, type: CONTRACT_TYPES.PERFORMANCE }
                    });
                }
            }
        );
    };
}

export function openComments(target) {
    return {
        type: CONTRACTS_REVIEW.OPEN,
        target
    };
}

export function closeComments() {
    return {
        type: CONTRACTS_REVIEW.CLOSE
    };
}

export function otherFieldInput(value, field) {
    return {
        type: CONTRACTS_REVIEW.INPUT,
        field,
        value
    };
}

export function commentInput(value) {
    return {
        type: CONTRACTS_REVIEW.INPUT,
        field: 'currentComment',
        value
    };
}

export function nameInput(value) {
    return {
        type: CONTRACTS_REVIEW.INPUT,
        field: 'signName',
        value
    };
}

export function functionInput(value) {
    return {
        type: CONTRACTS_REVIEW.INPUT,
        field: 'signFunction',
        value
    };
}

export function emailInput(value) {
    return {
        type: CONTRACTS_REVIEW.INPUT,
        field: 'signEmail',
        value
    };
}

export function commentAdd(elementId, value) {
    return {
        type: CONTRACTS_REVIEW.CHANGE,
        elementId,
        value
    };
}

export function toggleSign() {
    return {
        type: CONTRACTS_REVIEW.TOGGLE
    };
}

export function startSubmit(submitMessage = '') {
    return {
        type: CONTRACTS_REVIEW.SUBMIT,
        submitMessage
    };
}

export function stopSubmit() {
    return {
        type: CONTRACTS_REVIEW.SUBMIT_DONE
    };
}

export function showReviewValidation() {
    return async dispatch => {
        dispatch(stopSubmit());
        dispatch(openFlash('Please fill in all the required fields in the contract.', 'warning'));
    };
}

const tamperingErrorHandler = (dispatch, rollback) => ({
    [NOT_ACCEPTABLE_CODE]: () => {
        dispatch(stopSubmit());
        dispatch(
            openFlash(
                "You're trying to make an adjustment in the contract, but you're not authorized to do so. It might be due to browser extensions or system software like translation plugins, accessibility tools, or screen readers that modify on-screen content.  Please reach out to your account administrator for assistance.",
                'error'
            )
        );
        const el = document.getElementById('ckp-contracts-review-text');
        if (el) {
            el.innerHTML = rollback;
        }
    }
});

export function submit(contractText, asDraft = false, rollback) {
    return async (dispatch, getState) => {
        dispatch(startSubmit);
        const review = reviewDataSelector(getState());

        let versionBody = {
            logo: get(review, 'contract.version.logo', ''),
            data: contractText,
            state: review.signed
                ? CONTRACT_STATE_SIGNED
                : asDraft
                  ? CONTRACT_STATE_SENT_DRAFT
                  : CONTRACT_STATE_FEEDBACK,
            comments: omit(review.comments, CONTRACT_STATE_SENT_DRAFT ? [] : ['admin']),
            userId: currentUserSelector(getState()).id
        };
        let contractBody = permitContractBody(review.contract, true);
        if (review.signed) {
            versionBody = {
                ...versionBody,
                data: versionBody.data
            };
            contractBody.signedBy = review.signName;
            contractBody.signedFunction = review.signFunction || '';
            contractBody.signedOn = timeToMs();
            contractBody.publicId = null;
        }
        const errorHandling = tamperingErrorHandler(dispatch, rollback);
        await apiActionCreator(
            dispatch,
            () => contracts.put(review.contract.id, contractBody),
            () => {
                return apiActionCreator(
                    dispatch,
                    () => contracts.postVersion(review.contract.id, versionBody),
                    () => {
                        dispatch(push(CONTRACTS_PATH));
                        dispatch(
                            openFlash(asDraft ? 'Contract saved as draft.' : 'Contract review successfully sent.')
                        );
                    },
                    errorHandling
                );
            },
            errorHandling
        );
    };
}

export function submitPublic(contractText, pinCode, rollback) {
    return async (dispatch, getState) => {
        dispatch(startSubmit);
        const review = reviewDataSelector(getState());
        const versionBody = {
            logo: get(review, 'contract.version.logo', ''),
            data: contractText,
            state: review.signed ? CONTRACT_STATE_SIGNED : CONTRACT_STATE_FEEDBACK,
            comments: omit(review.comments, ['admin']),
            userId: currentUserSelector(getState()).id || '',
            anonName: review.signName,
            anonFunction: review.signFunction || '',
            anonEmail: review.signEmail
        };
        if (review.signed) {
            versionBody.signedFunction = review.signFunction || '';
            versionBody.signedBy = review.signName;
            versionBody.signedOn = timeToMs();
        }

        const errorHandling = tamperingErrorHandler(dispatch, rollback);
        return apiActionCreator(
            dispatch,
            () => contracts.postPublic(review.contract.id, versionBody),
            () => {
                dispatch(stopSubmit());
                if (review.signed) {
                    dispatch(push(REVIEW_SUCCESS_PATH));
                } else {
                    dispatch(openFlash('Contract review successfully sent.'));
                    getMember(review.contract.id, true, false, pinCode || '')(dispatch, getState);
                }
            },
            errorHandling
        );
    };
}

export function switchVersion(versionId, comments = false) {
    return (dispatch, getState) => {
        const contract = contractSelector(getState());
        const newVersion = find(contract.versions, c => c.id === versionId);
        if (newVersion) {
            const parsedNew = {
                ...newVersion,
                logo: bufferArrayToUtf8((newVersion.logo && newVersion.logo.data) || [])
            };
            dispatch({ type: CONTRACTS_REVIEW.SWITCH, version: parsedNew });
            dispatch({
                type: CONTRACTS_REVIEW.INPUT,
                field: 'comments',
                value: comments || parsedNew.comments || {}
            });
        }
    };
}
